import { useState } from 'react';

const API_BASE = import.meta.env.VITE_API_BASE ?? 'https://coder.wowerix.com';

interface TelegramQRProps {
  /** Tên bot Telegram (không có @) — vd: "ericoder_bot" */
  defaultBotName?: string;
}

export function TelegramQR({ defaultBotName = '' }: TelegramQRProps) {
  const [botName, setBotName] = useState(
    () => localStorage.getItem('tg_bot_name') ?? defaultBotName
  );
  const [editing, setEditing] = useState(!botName);
  const [draft, setDraft] = useState(botName);
  
  // Webhook Registration states
  const [botToken, setBotToken] = useState(() => localStorage.getItem('tg_bot_token') ?? '');
  const [isSaving, setIsSaving] = useState(false);

  const save = async () => {
    const clean = draft.replace(/^@/, '').trim();
    if (!clean || !botToken.trim()) return;

    setIsSaving(true);
    try {
      const res = await fetch(`${API_BASE.replace('/v1', '')}/api/telegram/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('ericoder_token')}`
        },
        body: JSON.stringify({ botToken })
      });
      
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || 'Lỗi đăng ký webhook');

      setBotName(clean);
      localStorage.setItem('tg_bot_name', clean);
      localStorage.setItem('tg_bot_token', botToken);
      setEditing(false);
    } catch (e: any) {
      alert(`Lỗi: ${e.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const botUrl = botName ? `https://t.me/${botName}` : '';
  const qrUrl  = botUrl
    ? `https://api.qrserver.com/v1/create-qr-code/?size=180x180&margin=12&format=png&data=${encodeURIComponent(botUrl)}`
    : '';

  return (
    <div style={{
      background: 'var(--bg-elevated)',
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius-md)',
      padding: '20px',
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {/* Telegram icon */}
          <div style={{
            width: 36, height: 36, borderRadius: '50%',
            background: 'linear-gradient(135deg, #229ED9, #1d7db5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '18px', flexShrink: 0,
          }}>✈️</div>
          <div>
            <div style={{ fontWeight: 700, fontSize: '14px' }}>Telegram Bot</div>
            <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Chat với Ericoder qua Telegram</div>
          </div>
        </div>
        {botName && !editing && (
          <button className="btn btn--secondary btn--sm" onClick={() => { setDraft(botName); setEditing(true); }}>
            ✎ Edit
          </button>
        )}
      </div>

      {/* Bot name editor */}
      {editing ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', background: 'var(--bg-card)', padding: '16px', borderRadius: '8px', border: '1px solid var(--border)' }}>
          <div>
            <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>
              1. Telegram Bot Nickname (không có @)
            </label>
            <input
              className="input"
              value={draft}
              onChange={e => setDraft(e.target.value)}
              placeholder="vd: ericoder_bot"
              style={{ width: '100%', fontSize: '13px' }}
              autoFocus
            />
          </div>
          <div>
            <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>
              2. Telegram Bot API Token
            </label>
            <input
              type="password"
              className="input"
              value={botToken}
              onChange={e => setBotToken(e.target.value)}
              placeholder="vd: 123456:ABC-DEF..."
              style={{ width: '100%', fontSize: '13px' }}
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '4px' }}>
            {botName && (
              <button className="btn btn--secondary btn--sm" onClick={() => { setDraft(botName); setEditing(false); }}>
                Cancel
              </button>
            )}
            <button className="btn btn--primary btn--sm" onClick={save} disabled={!draft.trim() || !botToken.trim() || isSaving}>
              {isSaving ? 'Đang lưu & đăng ký...' : 'Save & Generate QR'}
            </button>
          </div>
        </div>
      ) : botName ? (
        <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
          {/* QR Code */}
          <div style={{
            flexShrink: 0,
            background: '#ffffff',
            borderRadius: '10px',
            padding: '6px',
            display: 'inline-flex',
          }}>
            <img
              src={qrUrl}
              alt={`QR code for t.me/${botName}`}
              width={150} height={150}
              style={{ display: 'block', borderRadius: '6px' }}
              loading="lazy"
            />
          </div>

          {/* Info */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', flex: 1 }}>
            <div>
              <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>Bot URL</div>
              <a href={botUrl} target="_blank" rel="noopener noreferrer" style={{
                color: '#229ED9', fontFamily: 'var(--font-mono)', fontSize: '13px',
                textDecoration: 'none', fontWeight: 600,
              }}>
                t.me/{botName}
              </a>
            </div>

            <div>
              <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>Tích hợp Tự động</div>
              <div style={{ fontSize: '12px', color: '#10b981', fontWeight: 600 }}>
                ✅ Webhook đã được đăng ký trên Gateway
              </div>
            </div>

            <div style={{
              background: 'rgba(34,158,217,0.08)',
              border: '1px solid rgba(34,158,217,0.2)',
              borderRadius: '8px',
              padding: '10px 12px',
              fontSize: '11px',
              color: 'var(--text-secondary)',
              lineHeight: 1.6,
            }}>
              📱 Quét QR code bằng camera để mở chat với bot.<br />
              Gửi <code style={{ color: '#229ED9' }}>/start</code> để bắt đầu, sau đó nhập prompt viết code.
            </div>
          </div>
        </div>
      ) : (
        <div style={{
          textAlign: 'center', padding: '20px',
          color: 'var(--text-muted)', fontSize: '12px',
          border: '1px dashed var(--border)',
          borderRadius: '8px',
        }}>
          Nhập tên bot Telegram để hiển thị QR code kết nối
        </div>
      )}
    </div>
  );
}
